import { ENV } from '../../config/runtimeEnv';
import axios from "axios";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

export type GenotypeExportFormat = "csv" | "pdf";

// Download the gene/SNP/allele export for a sample as CSV
export const fetchGenotypeExportCsv = async (sampleId: string): Promise<Blob> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/samples/${encodeURIComponent(sampleId)}/genotype-export/csv`,
		{
			responseType: 'blob',
		}
	);

	return response.data;
};

// Download the gene/SNP/allele export for a sample as PDF
export const fetchGenotypeExportPdf = async (sampleId: string): Promise<Blob> => {
	const response = await http.get(
		`${API_BASE_URL}/reports/samples/${encodeURIComponent(sampleId)}/genotype-export/pdf`,
		{
			responseType: 'blob',
		}
	);

	return response.data;
};

// Reaches a `download` attribute, so anything outside [A-Za-z0-9._-] is stripped rather than escaped
const sanitiseSampleId = (sampleId: string): string => sampleId.replace(/[^A-Za-z0-9._-]/g, "");

const localDateStamp = (): string => {
	const now = new Date();
	const pad = (value: number) => String(value).padStart(2, "0");

	return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// Built client-side because labiq's CorsMiddleware sets no Access-Control-Expose-Headers, so a
// cross-origin response cannot read Content-Disposition.
export const genotypeExportFilename = (
	sampleId: string,
	format: GenotypeExportFormat
): string => `gene-codes-${sanitiseSampleId(sampleId)}-${localDateStamp()}.${format}`;

export const downloadGenotypeExport = async (
	sampleId: string,
	format: GenotypeExportFormat
): Promise<void> => {
	const blob = format === "csv"
		? await fetchGenotypeExportCsv(sampleId)
		: await fetchGenotypeExportPdf(sampleId);

	const url = window.URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = url;
	a.download = genotypeExportFilename(sampleId, format);
	document.body.appendChild(a);

	try {
		a.click();
	} finally {
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}
};

// responseType 'blob' makes an error body a Blob too, so only the status is readable here
export const genotypeExportErrorMessage = (error: unknown): string => {
	const status = axios.isAxiosError(error) ? error.response?.status : undefined;

	switch (status) {
		case 401:
			return "Your session has expired. Please sign in again.";
		case 404:
			return "This export is not available for that sample.";
		case 503:
			return "The authentication server is temporarily unavailable. Please try again shortly.";
		default:
			return "The genotype export could not be generated. Please try again.";
	}
};
